import isEmpty from 'lodash/isEmpty'
import {
    Dispatch, SetStateAction,
    useCallback, useMemo,
} from 'react'
import {
    State,
} from 'app/PoC/DataGridDemoPages/ContractBasisDemo/ContractBasisDemoCreateQuote/useContractBasisCreateQuoteReducer'
import useQuotePreview from 'app/Apps/Pricing/ContractBasis/hooks/useQuotePreview'
import {
    QuotePreviewRequestParams,
    AdditionalFeesTableRow,
    LanesTableRow, LanesTableRowUniq,
    PrintPreview,
    PrintPreviewUniq,
} from 'app/Apps/Pricing/ContractBasis/utils/createQuote.types'
import idConvertions from 'app/Apps/Pricing/ContractBasis/utils/idConvertions'
import {
    LaneQuoteViewType,
} from 'app/enums/label.enums'
import PrintFiltersForm from 'app/Apps/Pricing/ContractBasis/components/PrintFiltersForm'
import {
    ContactsSelection,
    PrintFilterValues,
} from 'app/Apps/Pricing/ContractBasis/components/PrintFiltersForm/PrintForm.types'
import Button from 'app/shared-components/Button'
import useContractBasisCreateQuoteContext
    from '../../ContractBasisCreateQuoteContext'

export default function FilterLaneAndFees({
    state,
    classes,
    showContactsSelections = false,
    setLaneFeesSelectedRows,
    setAdditionFeesSelectedRows,
    setFiltersState,
    setPreviewData,
    setContactsSelection,
}: Readonly<{
    state: State,
    classes: Record<string, string>,
    showContactsSelections?: boolean,
    setLaneFeesSelectedRows: Dispatch<SetStateAction<string[]>>,
    setAdditionFeesSelectedRows: Dispatch<SetStateAction<number[]>>,
    setFiltersState: Dispatch<SetStateAction<Partial<PrintFilterValues>>>,
    setPreviewData: Dispatch<SetStateAction<PrintPreviewUniq>>,
    setContactsSelection: Dispatch<SetStateAction<ContactsSelection>>,
}>) {
    const {
        contractBasisId,
        activeStep,
        setActiveStep,
        quoteId,
    } = useContractBasisCreateQuoteContext()

    const {
        laneGroups,
        additionalFee,
        filtersState,
        contactsSelection,
    } = state

    const {
        mutate: preview,
    } = useQuotePreview()

    const paramsForPreview = useMemo(() => {
        const {
            selectedAirports,
            selectedLaneFeesStatuses,
            selectedCustomerExecution,
            exactDates,
            laneFeeValidFrom,
            laneFeeValidTo,
            selectedLaneStatus,
            isCustomerQuotation,
            allowDuplication,
            comment,
        } = filtersState

        return {
            additionalFees: additionalFee,
            laneGroups,
            selectedAirports: selectedAirports?.map(idConvertions),
            selectedLaneFeesStatuses,
            selectedCustomerExecution: selectedCustomerExecution && {
                id: selectedCustomerExecution,
            },
            exactDates,
            laneFeeValidFrom,
            laneFeeValidTo,
            selectedLaneStatus,
            isCustomerQuotation,
            allowDuplication,
            skyCellContact: contactsSelection?.skyCellContact,
            customerContact: contactsSelection?.customerExecutionContact
                || contactsSelection?.customerQuotationContact,
            comment,
            quoteId,
        } as unknown as QuotePreviewRequestParams
    }, [
        filtersState,
        additionalFee,
        laneGroups,
        contactsSelection,
        quoteId,
    ])

    const onPreviewSuccess = useCallback((response: PrintPreview) => {
        const modifiedResponse: PrintPreviewUniq = {
            ...response,
            lanesTableRows: response.lanesTableRows.map((row: LanesTableRow) => {
                return {
                    ...row,
                    id: `${row.laneNumber}.${row.laneFees.laneFeesNumber}`,
                    tempId: `${row.laneNumber}.${row.laneFees.laneFeesNumber}`,
                }
            }),
            laneQuoteViewType: LaneQuoteViewType.DETAILED_VIEW,
        }

        const allFeesIds = modifiedResponse.additionalFeesTableRows
            .map((item: AdditionalFeesTableRow) => {
                return item.additionalFeesFeeId
            })

        const allLaneIds = modifiedResponse.lanesTableRows.map((item: LanesTableRowUniq) => {
            return item.tempId
        })

        setPreviewData(modifiedResponse)
        setAdditionFeesSelectedRows(allFeesIds)
        setLaneFeesSelectedRows(allLaneIds)
        setActiveStep(2)
    }, [
        setActiveStep,
        setAdditionFeesSelectedRows,
        setLaneFeesSelectedRows,
        setPreviewData,
    ])

    const previewCall = useCallback(() => {
        preview({
            ...paramsForPreview,
        }, {
            onSuccess: onPreviewSuccess,
        })
    }, [
        preview,
        onPreviewSuccess,
        paramsForPreview,
    ])

    const isContactsSelectionsSelected = useMemo((): boolean => {
        return !showContactsSelections
            || !isEmpty(contactsSelection.skyCellContact)
    }, [
        contactsSelection,
        showContactsSelections,
    ])

    return (
        <div>
            <PrintFiltersForm
                contractId={contractBasisId}
                state={filtersState}
                setState={setFiltersState}
                laneGroups={laneGroups}
                disabled={activeStep !== 1}
                contactsSelections={contactsSelection}
                onContactsSelectionsChange={setContactsSelection}
                showContactsSelections={showContactsSelections}
            />
            <div>
                {activeStep === 1 && (
                    <div className={classes.doubleButtons}>
                        <Button
                            label="Continue"
                            onClick={previewCall}
                            className={classes.button}
                            disabled={!isContactsSelectionsSelected}
                        />
                        <Button
                            label="Back"
                            secondary
                            onClick={() => {
                                setActiveStep(0)
                            }}
                            className={classes.button}
                        />
                    </div>
                )}
                {activeStep !== 1 && (
                    <Button
                        label="Edit"
                        onClick={() => {
                            setActiveStep(1)
                        }}
                        className={classes.button}
                    />
                )}
            </div>
        </div>
    )
}
